import type { WidgetConfig } from "./client";
import { launcherShape, presetOf, PRESETS, type PresetId } from "./presets";

/**
 * The stylesheets behind the look presets, appended after the base sheet in
 * styles.ts.
 *
 * Only the chosen preset's rules are generated, so a visitor downloads the
 * source of all eleven once (inside widget.js) but the browser only ever parses
 * one. "classic" with no fine-tuning generates nothing at all: the base sheet
 * IS classic, and a site that never touched these settings gets the same
 * bytes it always had (pinned by tests/styles.test.ts).
 *
 * Order matters and is fixed: glue → palette → preset rules → fine-tuning →
 * tail. Everything is plain selectors of the same specificity as the base
 * sheet, so "later wins" is the only rule to keep in mind; the tail restates
 * the phone layout and reduced motion because a preset's desktop rules would
 * otherwise override the base sheet's media queries.
 *
 * The CSS is written compactly on purpose — this text ships to every visitor.
 */

/* System stacks only: nothing here ever makes a network request. */
const SYSTEM = `-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif`;
const ROUNDED = `ui-rounded,"SF Pro Rounded","Hiragino Maru Gothic ProN",Quicksand,Comfortaa,Manjari,"Arial Rounded MT Bold",Calibri,sans-serif`;
const SERIF = `Charter,"Bitstream Charter","Sitka Text",Cambria,Georgia,serif`;
const OLDSTYLE = `"Iowan Old Style","Palatino Linotype","URW Palladio L",P052,Georgia,serif`;
const MONO = `ui-monospace,"Cascadia Mono","SF Mono",Menlo,Consolas,"DejaVu Sans Mono",monospace`;
const HUMANIST = `Seravek,"Gill Sans Nova",Ubuntu,Calibri,"DejaVu Sans",sans-serif`;
const GEOMETRIC = `Avenir,"Avenir Next LT Pro",Montserrat,Corbel,"URW Gothic",sans-serif`;
const INDUSTRIAL = `Bahnschrift,"DIN Alternate","Franklin Gothic Medium","Nimbus Sans Narrow",sans-serif`;
const HEAVY = `"Arial Black","Helvetica Neue",Arial,sans-serif`;

export const FONT_STACKS = { system: SYSTEM, rounded: ROUNDED, serif: SERIF, mono: MONO } as const;

/** A chamfer: two opposite corners cut at 45°. */
const cut = (size: string): string =>
  `polygon(${size} 0,100% 0,100% calc(100% - ${size}),calc(100% - ${size}) 100%,0 100%,0 ${size})`;

/**
 * The speech-bubble mark: three rounded corners and one near-square "tail"
 * corner. `at` names which bottom corner is the tail, so an operator bubble
 * and a visitor bubble can mirror each other (aurora).
 */
const bubbleCorner = (at: "left" | "right", round: string, tail: string): string =>
  at === "right" ? `${round} ${round} ${tail} ${round}` : `${round} ${round} ${round} ${tail}`;

interface Sheet {
  /** Custom properties for the light palette, plus radii and fonts. */
  light: string;
  /** Colours only; applied according to the site's `theme` setting. */
  dark: string;
  /** Structural rules. `s` is the launcher's side, `o` the other one. */
  css: (s: "left" | "right", o: "left" | "right") => string;
}

const SHEETS: Record<PresetId, Sheet> = {
  // Only reached when a classic site fine-tunes something: these are the
  // values the base sheet hard-codes, restated as variables the tuning can move.
  classic: {
    light: `--cc-rb:14px;--cc-rc:12px;--cc-font:${SYSTEM};--cc-font-head:var(--cc-font);`,
    dark: ``,
    css: () => ``,
  },

  /* Paper — flat, shadowless, hairlines; one ink; an underlined input. Calm. */
  paper: {
    light: `--cc-bg:#ffffff;--cc-surface:#ffffff;--cc-text:#1c1c1e;--cc-muted:#5f5f66;--cc-border:#e1e1e6;--cc-shadow:none;--cc-radius:12px;--cc-rb:10px;--cc-rc:8px;--cc-font:${GEOMETRIC};--cc-font-head:var(--cc-font);`,
    dark: `--cc-bg:#1a1a1c;--cc-surface:#1a1a1c;--cc-text:#f2f2f4;--cc-muted:#a3a3ab;--cc-border:#35353b;`,
    css: () => `
.launcher{box-shadow:none;transition:opacity 160ms ease}
.launcher:hover{transform:none;opacity:.88}
.panel{box-shadow:none;animation:cc-fade 220ms ease}
@keyframes cc-fade{from{opacity:0}}
.header{background:var(--cc-bg);color:var(--cc-text);border-bottom:1px solid var(--cc-border);padding:18px 18px 16px}
.header .avatar{background:none;border:1px solid var(--cc-border)}
.header h2{font-size:15px;font-weight:600}
.header .close:hover{background:rgba(127,127,127,.14)}
.status{color:var(--cc-muted);opacity:1}
.messages{padding:20px 18px;gap:14px}
.bubble{background:none;padding:9px 13px}
.quick-reply{border-color:var(--cc-border);color:var(--cc-text);background:none;border-radius:var(--cc-rc)}
.faq-item{background:none}
.composer{padding:6px 16px 12px;align-items:center}
.composer textarea{border:0;border-bottom:1px solid var(--cc-border);border-radius:0;background:none;padding:10px 2px}
.composer textarea:focus{outline:none;border-bottom-color:var(--cc-primary);box-shadow:0 1px 0 var(--cc-primary)}
.composer button.send{background:none;color:var(--cc-primary);width:36px;height:36px;font-size:18px}
.teaser{box-shadow:none}
`,
  },

  /* Neon — a HUD: chamfered corners, a lit frame, a faint grid, mono read-outs. */
  neon: {
    light: `--cc-bg:#f5f6fb;--cc-surface:#eceef7;--cc-text:#0e1124;--cc-muted:#4c5375;--cc-border:#c9cde2;--cc-shadow:none;--cc-radius:14px;--cc-rb:8px;--cc-rc:4px;--cc-font:${SYSTEM};--cc-font-head:${INDUSTRIAL};`,
    dark: `--cc-bg:#0c0e1b;--cc-surface:#141831;--cc-text:#e7e9ff;--cc-muted:#949ac2;--cc-border:#2a3054;`,
    css: () => `
.launcher{position:relative;isolation:isolate;width:58px;height:58px;border-radius:0;background:transparent;box-shadow:none;filter:drop-shadow(0 6px 14px rgba(0,0,0,.35));filter:drop-shadow(0 0 10px color-mix(in srgb,var(--cc-primary) 70%,transparent));transition:transform 160ms ease}
.launcher::before{content:"";position:absolute;inset:0;z-index:-1;background:var(--cc-primary);clip-path:${cut("var(--cc-radius)")}}
.launcher:hover{transform:translateY(-2px)}
.badge{border-radius:0}
.panel,.teaser{bottom:78px}
.panel{border:1px solid var(--cc-primary);border-radius:0;clip-path:${cut("var(--cc-radius)")};background:var(--cc-bg);background:radial-gradient(120% 55% at 100% 0,color-mix(in srgb,var(--cc-primary) 15%,transparent),transparent 70%),var(--cc-bg);animation:cc-crt 280ms cubic-bezier(.2,.9,.3,1)}
.panel::before,.panel::after{content:"";position:absolute;z-index:3;width:var(--cc-radius);height:var(--cc-radius);pointer-events:none;background:linear-gradient(135deg,transparent calc(50% - 1px),var(--cc-primary) calc(50% - 1px),var(--cc-primary) calc(50% + 1px),transparent calc(50% + 1px))}
.panel::before{top:-1px;left:-1px}
.panel::after{bottom:-1px;right:-1px}
@keyframes cc-crt{0%{opacity:0;transform:scaleY(.02)}60%{opacity:1;transform:scaleY(1.01)}75%{opacity:.75}100%{opacity:1;transform:none}}
.header{position:relative;background:none;color:var(--cc-text);border-bottom:1px solid var(--cc-border);padding:16px 18px}
.header::after{content:"";position:absolute;left:18px;bottom:-1px;width:72px;height:2px;background:var(--cc-primary);box-shadow:0 0 12px var(--cc-primary)}
.header .avatar{border-radius:0;background:var(--cc-surface);border:1px solid var(--cc-border);clip-path:${cut("8px")}}
.header h2{font-size:17px;letter-spacing:.04em}
.header .close{border-radius:0}
.header .close:hover{background:rgba(127,127,127,.16)}
.status{font-family:${MONO};font-size:11.5px;letter-spacing:.04em;color:var(--cc-muted);opacity:1}
.dot{border-radius:0;box-shadow:0 0 8px #34d399}
.messages{background:none;background-image:linear-gradient(color-mix(in srgb,var(--cc-border) 45%,transparent) 1px,transparent 1px),linear-gradient(90deg,color-mix(in srgb,var(--cc-border) 45%,transparent) 1px,transparent 1px);background-size:32px 32px}
.bubble{background:var(--cc-surface);border-width:1px 1px 1px 2px;border-left-color:var(--cc-primary);border-radius:0;clip-path:polygon(0 0,100% 0,100% calc(100% - var(--cc-rb)),calc(100% - var(--cc-rb)) 100%,0 100%)}
.row.visitor .bubble{border:0;clip-path:polygon(0 0,100% 0,100% 100%,var(--cc-rb) 100%,0 calc(100% - var(--cc-rb)))}
.bubble.typing{clip-path:none}
.typing span{border-radius:0;background:var(--cc-primary)}
.meta{font-family:${MONO};font-size:10.5px;letter-spacing:.03em}
.op-avatar{border-radius:0}
.quick-reply{border-radius:0;background:var(--cc-bg);color:var(--cc-text);font-family:${MONO};font-size:13px}
.quick-reply:hover,.quick-reply:focus-visible{box-shadow:0 0 14px -2px var(--cc-primary)}
.faq-item,.faq-item summary,.thumb,.system,.teaser{border-radius:0}
.faq-item{background:var(--cc-surface)}
.composer{background:none}
.composer textarea{border-radius:0;background:var(--cc-surface)}
.composer textarea:focus{outline:1px solid var(--cc-primary);box-shadow:0 0 12px -4px var(--cc-primary)}
.composer button.send{border-radius:0;clip-path:${cut("8px")}}
.teaser{border-color:var(--cc-primary);box-shadow:none}
`,
  },

  /* Glass — frosted, translucent, a header that fades into the thread. */
  glass: {
    // --cc-muted is darkened/lightened a notch past what the flat presets need:
    // .panel is translucent (--cc-glass, composited straight over whatever the
    // host page shows through the backdrop-filter blur — see themes.ts' glass
    // .panel rule), so muted text on it has to hold 4.5:1 against that
    // composite at both extremes of an unknown page background, not just
    // against --cc-bg. See tests/presets.test.ts's "contrast" describe block.
    light: `--cc-bg:#ffffff;--cc-glass:rgba(250,251,255,.72);--cc-surface:rgba(255,255,255,.55);--cc-text:#10131a;--cc-muted:#3f4553;--cc-border:rgba(20,24,40,.12);--cc-shadow:0 24px 60px -12px rgba(20,24,48,.35);--cc-radius:24px;--cc-rb:18px;--cc-rc:14px;--cc-font:${SYSTEM};--cc-font-head:var(--cc-font);`,
    dark: `--cc-bg:#1d2130;--cc-glass:rgba(22,25,36,.8);--cc-surface:rgba(18,20,30,.5);--cc-text:#f1f3f9;--cc-muted:#b4bac9;--cc-border:rgba(255,255,255,.1);--cc-shadow:0 24px 60px -12px rgba(0,0,0,.6);`,
    css: (s) => `
.launcher{width:58px;height:58px;background-image:linear-gradient(150deg,rgba(255,255,255,.38),rgba(255,255,255,0) 55%);box-shadow:0 10px 28px -6px rgba(20,24,48,.45),inset 0 1px 0 rgba(255,255,255,.5),inset 0 0 0 1px rgba(255,255,255,.18)}
.panel,.teaser{bottom:76px}
.panel{box-shadow:var(--cc-shadow),inset 0 1px 0 rgba(255,255,255,.35);transform-origin:bottom ${s};animation:cc-glass 280ms cubic-bezier(.2,.8,.2,1)}
@supports ((-webkit-backdrop-filter:blur(1px)) or (backdrop-filter:blur(1px))){.panel,.teaser{background:var(--cc-glass);-webkit-backdrop-filter:blur(22px) saturate(1.7);backdrop-filter:blur(22px) saturate(1.7)}}
@keyframes cc-glass{from{opacity:0;transform:translateY(12px) scale(.97)}}
.header{position:relative;z-index:1;margin-bottom:-22px;padding:18px 18px 36px;background:linear-gradient(180deg,var(--cc-primary) 68%,transparent);background:linear-gradient(180deg,var(--cc-primary) 66%,color-mix(in srgb,var(--cc-primary) 40%,transparent) 84%,transparent)}
.header + .messages{padding-top:38px}
.header + .error{margin-top:30px}
.header + .offline-band,.header + .offline,.header + .prechat{padding-top:34px}
.header .avatar{box-shadow:inset 0 0 0 1px rgba(255,255,255,.35)}
.header .close{border-radius:50%}
.bubble{box-shadow:0 1px 2px rgba(20,24,48,.08);border-bottom-left-radius:6px}
.row.visitor .bubble{background-image:linear-gradient(160deg,rgba(255,255,255,.16),rgba(255,255,255,0));border-bottom-left-radius:var(--cc-rb);border-bottom-right-radius:6px}
.bubble.typing{border-bottom-left-radius:6px}
.quick-reply{background:var(--cc-bg);border-color:var(--cc-border);color:var(--cc-text)}
.composer{background:none}
.composer textarea{background:var(--cc-bg)}
.composer button.send{border-radius:50%}
`,
  },

  /* Bubble — soft and round, a rounded face, a springy open, messages that pop. */
  bubble: {
    light: `--cc-bg:#ffffff;--cc-surface:#f5f3fa;--cc-text:#1f1a2e;--cc-muted:#5f5874;--cc-border:#ebe7f3;--cc-shadow:0 18px 50px -10px rgba(40,20,80,.3);--cc-radius:28px;--cc-rb:22px;--cc-rc:22px;--cc-font:${ROUNDED};--cc-font-head:var(--cc-font);`,
    dark: `--cc-bg:#221e2d;--cc-surface:#18151f;--cc-text:#f5f2fc;--cc-muted:#aca6c2;--cc-border:#373048;--cc-shadow:0 18px 50px -10px rgba(0,0,0,.6);`,
    css: (s) => `
.launcher{width:62px;height:62px;transition:transform 380ms cubic-bezier(.34,1.56,.64,1)}
.launcher:hover{transform:scale(1.1) rotate(-6deg)}
.panel,.teaser{bottom:80px}
.panel{border:0;background:var(--cc-surface);background:color-mix(in srgb,var(--cc-primary) 7%,var(--cc-surface));transform-origin:bottom ${s};animation:cc-pop 440ms cubic-bezier(.34,1.56,.64,1)}
@keyframes cc-pop{0%{opacity:0;transform:scale(.5) translateY(24px)}50%{opacity:1}100%{transform:none}}
.header{position:relative;z-index:1;padding:20px 20px 24px;border-radius:0 0 26px 26px;box-shadow:0 10px 24px -14px rgba(40,20,80,.45)}
.header .avatar{width:44px;height:44px;box-shadow:0 0 0 3px rgba(255,255,255,.35)}
.header h2{font-size:18px;font-weight:700}
.header .close{border-radius:50%}
.messages{background:none;padding:18px 16px;gap:12px}
.bubble{border:0;padding:11px 16px;box-shadow:0 2px 8px -3px rgba(40,20,80,.18);border-bottom-left-radius:6px}
.row.visitor .bubble{border-bottom-left-radius:var(--cc-rb);border-bottom-right-radius:6px}
.row{animation:cc-rise 300ms cubic-bezier(.34,1.56,.64,1)}
@keyframes cc-rise{from{opacity:0;transform:translateY(10px) scale(.95)}}
.typing span{background:var(--cc-primary);animation:cc-hop 1s infinite ease-in-out}
@keyframes cc-hop{0%,60%,100%{transform:none;opacity:.45}30%{transform:translateY(-5px);opacity:1}}
.quick-reply{border:2px solid var(--cc-border);background:var(--cc-bg);color:var(--cc-text);font-weight:600;padding:8px 16px}
.quick-reply{border-color:transparent;background:color-mix(in srgb,var(--cc-primary) 14%,var(--cc-bg))}
.faq-item{border:0}
.composer{border-top:0;padding:12px;gap:10px;align-items:center}
.composer textarea{border:0;padding:11px 16px;background:var(--cc-surface)}
.composer button.send{border-radius:50%;width:44px;height:44px;transition:transform 300ms cubic-bezier(.34,1.56,.64,1)}
.composer button.send:not(:disabled):hover{transform:scale(1.08) rotate(-8deg)}
.teaser{border:0;border-bottom-${s}-radius:6px}
`,
  },

  /* Terminal — no bubbles: a session log, a prompt, a block cursor for "typing". */
  terminal: {
    light: `--cc-bg:#f7f7f5;--cc-surface:#ececea;--cc-text:#1f2328;--cc-muted:#57606a;--cc-border:#d3d7dc;--cc-shadow:0 14px 40px rgba(0,0,0,.22);--cc-radius:4px;--cc-rb:0px;--cc-rc:2px;--cc-font:${MONO};--cc-font-head:var(--cc-font);font-size:13.5px;line-height:1.55;`,
    dark: `--cc-bg:#1d2021;--cc-surface:#282828;--cc-text:#ebdbb2;--cc-muted:#a89984;--cc-border:#3c3836;--cc-shadow:0 14px 40px rgba(0,0,0,.5);`,
    css: () => `
.launcher{border-radius:var(--cc-rc)}
.launcher svg{display:none}
.launcher::after{content:">_";content:">_" / "";order:-1;font-family:${MONO};font-weight:700;font-size:19px;letter-spacing:-.04em}
.launcher:hover{transform:translateY(-2px)}
.panel{width:420px;animation:cc-draw 240ms steps(5,end)}
@keyframes cc-draw{from{clip-path:inset(0 0 100% 0)}to{clip-path:inset(0)}}
.header{background:var(--cc-surface);color:var(--cc-text);padding:10px 12px 10px 14px;border-bottom:1px solid var(--cc-border);box-shadow:inset 0 2px 0 var(--cc-primary)}
.header .avatar{display:none}
.header h2{font-size:13.5px;font-weight:700}
.header h2::before{content:"$ ";content:"$ " / "";color:var(--cc-muted)}
.header .close{border-radius:2px}
.header .close:hover{background:rgba(127,127,127,.18)}
.status{font-size:12px;color:var(--cc-muted);opacity:1}
.dot{border-radius:0}
.messages{background:var(--cc-bg);gap:12px;padding:14px 16px}
.row{display:block}
.row > div{max-width:none;display:flex;flex-direction:column}
.row .op-avatar{display:none}
.meta,.row.visitor .meta{order:-1;margin:0 0 1px;font-size:11.5px;text-align:left}
.bubble,.row.visitor .bubble{position:relative;background:none;border:0;border-radius:0;padding:0 0 0 2ch;color:var(--cc-text)}
.bubble::before{content:"<";content:"<" / "";position:absolute;left:0;color:var(--cc-muted)}
.row.visitor .bubble::before{content:">";content:">" / "";color:var(--cc-primary);font-weight:700}
.row.visitor .bubble a{color:inherit}
.row.visitor .attachments{justify-content:flex-start}
.attachments,.progress{margin-left:2ch}
.thumb{border-radius:0}
.typing{padding:0 0 0 2ch}
.typing span{width:.6em;height:1.15em;border-radius:0;background:var(--cc-text);animation:cc-cursor 1s steps(1) infinite}
.typing span + span{display:none}
@keyframes cc-cursor{50%{opacity:0}}
.system{align-self:stretch;text-align:center;background:none;border:0;border-radius:0;padding:0}
.system::before{content:"-- ";content:"-- " / ""}
.system::after{content:" --";content:" --" / ""}
.quick-reply{border-radius:0;border-color:var(--cc-border);background:none;color:var(--cc-text);font-size:13px;padding:4px 10px}
.faq-item,.faq-item summary{border-radius:0;background:none}
.faq-title{text-transform:none;letter-spacing:0}
.composer{background:var(--cc-surface);align-items:center;padding:8px 10px}
.composer:focus-within{box-shadow:inset 0 0 0 1px var(--cc-primary)}
.composer::before{content:">";content:">" / "";order:1;color:var(--cc-primary);font-weight:700;padding-left:4px}
.attach{order:0}
.composer textarea{order:2;border:0;border-radius:0;background:none;padding:8px 4px;min-height:36px;caret-color:var(--cc-primary)}
.composer textarea:focus{outline:none}
.composer button.send{order:3;width:36px;height:34px}
.teaser{border-radius:var(--cc-radius)}
`,
  },

  /* Corporate — a two-tier colour band, tight spacing, square corners and launcher. */
  corporate: {
    light: `--cc-bg:#ffffff;--cc-surface:#f3f5f8;--cc-text:#0f1b2d;--cc-muted:#526073;--cc-border:#d8dee7;--cc-shadow:0 2px 4px rgba(15,27,45,.06),0 12px 28px rgba(15,27,45,.16);--cc-radius:8px;--cc-rb:6px;--cc-rc:4px;--cc-font:${HUMANIST};--cc-font-head:var(--cc-font);font-size:14px;`,
    dark: `--cc-bg:#121b29;--cc-surface:#0c1420;--cc-text:#e6ecf4;--cc-muted:#98a5b8;--cc-border:#263447;--cc-shadow:0 2px 4px rgba(0,0,0,.2),0 12px 28px rgba(0,0,0,.5);`,
    css: () => `
.launcher{width:54px;height:54px;border-radius:var(--cc-radius);box-shadow:0 6px 16px rgba(15,27,45,.28)}
.launcher:hover{transform:translateY(-2px)}
.panel{animation:cc-slide 200ms cubic-bezier(.2,0,0,1)}
@keyframes cc-slide{from{opacity:0;transform:translateY(16px)}}
.header{flex-wrap:wrap;row-gap:12px;padding:14px 16px 0}
.header > div{display:contents}
.header h2{flex:1;font-size:15px}
.header .avatar{width:32px;height:32px;border-radius:var(--cc-rc)}
.header .close{border-radius:var(--cc-rc)}
.status{order:3;flex:1 0 calc(100% + 32px);margin:0 -16px;padding:7px 16px;background:rgba(0,0,0,.18);font-size:12px;opacity:1}
.messages{padding:14px;gap:8px}
.bubble{padding:8px 12px}
.op-avatar{width:26px;height:26px;border-radius:var(--cc-rc)}
.quick-reply{border-radius:var(--cc-rc);padding:6px 12px;font-size:13px}
.composer{padding:10px 12px}
.composer textarea{background:var(--cc-bg);min-height:40px}
.composer button.send{width:40px;height:40px}
.system{border-radius:var(--cc-rc)}
`,
  },

  /* Editorial — a masthead, an interview transcript, rules instead of bubbles. */
  editorial: {
    light: `--cc-bg:#fbf7ef;--cc-surface:#f5efe3;--cc-text:#221d18;--cc-muted:#675d50;--cc-border:#e0d6c3;--cc-shadow:0 18px 40px -8px rgba(60,40,20,.28);--cc-radius:6px;--cc-rb:2px;--cc-rc:2px;--cc-font:${SERIF};--cc-font-head:${OLDSTYLE};font-size:15.5px;line-height:1.6;`,
    dark: `--cc-bg:#1b1916;--cc-surface:#23201b;--cc-text:#efe7da;--cc-muted:#ada291;--cc-border:#3d372e;--cc-shadow:0 18px 40px -8px rgba(0,0,0,.6);`,
    css: () => `
.launcher{box-shadow:0 0 0 3px var(--cc-bg),0 0 0 4px var(--cc-primary),0 10px 24px -6px rgba(60,40,20,.45)}
.launcher:hover{transform:translateY(-2px)}
.panel{width:400px;animation:cc-rise 320ms cubic-bezier(.2,.7,.2,1)}
@keyframes cc-rise{from{opacity:0;transform:translateY(10px)}}
.header{background:var(--cc-bg);color:var(--cc-text);padding:20px 22px 14px;border-bottom:3px double var(--cc-border);align-items:flex-end}
.header .avatar{width:40px;height:40px;background:var(--cc-surface);border:1px solid var(--cc-border);font-family:var(--cc-font-head);font-style:italic;font-size:18px}
.header h2{font-size:23px;font-weight:600;line-height:1.15;letter-spacing:-.01em}
.header .close{align-self:flex-start}
.header .close:hover{background:rgba(127,127,127,.14)}
.status{font-style:italic;color:var(--cc-muted);opacity:1;font-size:13.5px;margin-top:4px}
.messages{background:var(--cc-bg);padding:20px 22px;gap:18px}
.row.visitor{justify-content:flex-start;padding-left:12%}
.row > div{max-width:100%;display:flex;flex-direction:column}
.row .op-avatar{display:none}
.meta,.row.visitor .meta{order:-1;margin:0 0 3px;text-align:left;font-variant:small-caps;letter-spacing:.05em;font-size:12.5px}
.bubble,.row.visitor .bubble{background:none;color:var(--cc-text);border:0;border-left:2px solid var(--cc-border);border-radius:0;padding:1px 0 1px 14px}
.row.visitor .bubble{border-left-color:var(--cc-primary);font-style:italic}
.row.visitor .bubble a{color:var(--cc-primary)}
.row.visitor .attachments{justify-content:flex-start}
.row:not(.agent):not(.visitor) .bubble{border:0;padding:0;font-family:var(--cc-font-head);font-size:20px;line-height:1.35;letter-spacing:-.005em}
.bubble.typing{padding:4px 0 4px 14px}
.system{background:none;border:0;font-style:italic}
.quick-replies{gap:4px 16px}
.quick-reply{border:0;border-bottom:1px solid var(--cc-primary);border-radius:0;background:none;color:var(--cc-text);padding:3px 1px;font-style:italic}
.quick-reply:hover,.quick-reply:focus-visible{background:var(--cc-surface);color:var(--cc-text)}
.faq-title{font-family:var(--cc-font-head);font-style:italic;font-weight:400;font-size:15px;letter-spacing:0;text-transform:none}
.faq-item{border:0;border-top:1px solid var(--cc-border);border-radius:0;background:none}
.faq-item summary{padding-left:0;font-weight:600}
.faq-item p{padding-left:0}
.composer{background:var(--cc-bg);padding:10px 18px 14px;align-items:center}
.composer:focus-within{box-shadow:inset 0 2px 0 var(--cc-primary)}
.composer textarea{border:0;border-radius:0;background:none;padding:10px 0}
.composer textarea:focus{outline:none}
.composer button.send{border-radius:50%;width:38px;height:38px}
`,
  },

  /* Brutal — thick ink outlines, hard offset shadows, heavy type, a pill that presses. */
  brutal: {
    light: `--cc-bg:#ffffff;--cc-surface:#efefef;--cc-text:#000000;--cc-muted:#383838;--cc-border:#000000;--cc-shadow:8px 8px 0 #000000;--cc-radius:0px;--cc-rb:0px;--cc-rc:0px;--cc-font:${SYSTEM};--cc-font-head:${HEAVY};`,
    dark: `--cc-bg:#141414;--cc-surface:#1f1f1f;--cc-text:#ffffff;--cc-muted:#cfcfcf;--cc-border:#f2f2f2;--cc-shadow:8px 8px 0 #f2f2f2;`,
    css: (s) => `
.launcher{width:auto;height:52px;padding:0 20px 0 16px;border-radius:999px;border:3px solid var(--cc-border);box-shadow:4px 4px 0 var(--cc-border);font-weight:800;transition:transform 110ms ease,box-shadow 110ms ease}
.launcher:hover{transform:translate(-2px,-2px);box-shadow:6px 6px 0 var(--cc-border)}
.launcher:active{transform:translate(4px,4px);box-shadow:0 0 0 var(--cc-border)}
.badge{border:2px solid var(--cc-border);border-radius:0}
.panel,.teaser{bottom:84px}
.panel{border:3px solid var(--cc-border);animation:cc-slam 200ms cubic-bezier(.2,1.4,.4,1)}
@keyframes cc-slam{from{opacity:0;transform:translate(8px,8px);box-shadow:0 0 0 var(--cc-border)}}
.header{border-bottom:3px solid var(--cc-border);padding:14px 16px}
.header h2{font-size:19px;font-weight:900;letter-spacing:-.01em}
.header .avatar{border-radius:0;border:2px solid currentColor;background:none;font-weight:900}
.header .close{border:2px solid currentColor;border-radius:0;font-weight:900}
.header .close:hover{background:var(--cc-on-primary);color:var(--cc-primary)}
.status{font-weight:700;opacity:1}
.dot{width:10px;height:10px;border:2px solid currentColor}
.messages{gap:14px;padding:18px 16px;background-image:radial-gradient(color-mix(in srgb,var(--cc-border) 22%,transparent) 1px,transparent 1.5px);background-size:14px 14px}
.bubble,.row.visitor .bubble{border:2px solid var(--cc-border);box-shadow:3px 3px 0 var(--cc-border);font-weight:500}
.meta{color:var(--cc-text);font-weight:700}
.op-avatar{border-radius:0;border:2px solid var(--cc-border)}
.quick-reply,.primary,.offline-send{border:2px solid var(--cc-border);box-shadow:3px 3px 0 var(--cc-border);font-weight:700}
.quick-reply{border-radius:var(--cc-rc);color:var(--cc-text)}
.quick-reply:active,.primary:active,.offline-send:active,.composer button.send:active{transform:translate(3px,3px);box-shadow:none}
.faq-item{border:2px solid var(--cc-border)}
.system{border:2px solid var(--cc-border);border-radius:0;color:var(--cc-text);font-weight:700}
.composer{border-top:3px solid var(--cc-border);padding:12px;gap:10px}
.composer textarea{border:2px solid var(--cc-border);background:var(--cc-bg);font-weight:500}
.composer textarea:focus{outline:none;box-shadow:3px 3px 0 var(--cc-primary)}
.composer button.send{border:2px solid var(--cc-border);box-shadow:3px 3px 0 var(--cc-border)}
.error{border:2px solid #991b1b;border-radius:0}
.teaser{border:3px solid var(--cc-border);box-shadow:5px 5px 0 var(--cc-border);border-bottom-${s}-radius:0}
`,
  },

  /* Dock — not a card: a full-height sheet on the page's edge, opened by a tab. */
  dock: {
    light: `--cc-bg:#ffffff;--cc-surface:#f4f5f7;--cc-text:#15181d;--cc-muted:#59616d;--cc-border:#e1e4e8;--cc-shadow:0 0 48px -8px rgba(15,20,30,.3);--cc-radius:0px;--cc-rb:12px;--cc-rc:10px;--cc-font:${SYSTEM};--cc-font-head:var(--cc-font);`,
    dark: `--cc-bg:#16191e;--cc-surface:#1d2127;--cc-text:#eef1f4;--cc-muted:#9ca5b0;--cc-border:#2d333b;--cc-shadow:0 0 48px -8px rgba(0,0,0,.7);`,
    css: (s, o) => `
.panel{position:fixed;top:0;bottom:0;${s}:0;width:400px;max-width:100vw;height:auto;max-height:none;border:0;border-${o}:1px solid var(--cc-border);border-radius:0;border-top-${o}-radius:var(--cc-radius);border-bottom-${o}-radius:var(--cc-radius);animation:cc-dock 300ms cubic-bezier(.2,.8,.2,1)}
@keyframes cc-dock{from{transform:translateX(${s === "right" ? "" : "-"}100%)}}
.header{position:relative;background:var(--cc-bg);color:var(--cc-text);padding:24px 22px 18px;border-bottom:1px solid var(--cc-border)}
.header::before{content:"";position:absolute;top:0;left:0;right:0;height:4px;background:var(--cc-primary)}
.header .avatar{width:42px;height:42px;background:var(--cc-primary);color:var(--cc-on-primary)}
.header h2{font-size:19px}
.header .close:hover{background:rgba(127,127,127,.14)}
.status{color:var(--cc-muted);opacity:1}
.messages{padding:20px 22px;gap:12px}
.composer{padding:14px 18px 18px}
`,
  },

  /*
   * Aurora — AgentlyDesk's own: navy ink, one marigold accent, and the
   * speech-bubble corner (three rounded, one near-square tail) carried from
   * the launcher through the panel, the bubbles and the composer.
   *
   * Two rules kept this preset honest:
   *
   *  - Every moving part lives inside `@media (prefers-reduced-motion:
   *    no-preference)` and every keyframe is written as a `from`/`to` off the
   *    resting state, so the look with motion switched off is the finished
   *    look, not a half-played one.
   *  - The panel, the thread and the bubbles are flat --cc-bg/--cc-surface —
   *    the aurora is in the header, not behind the text. That is what lets
   *    tests/presets.test.ts check this palette against plain hex surfaces
   *    and mean it.
   *
   * The header cannot use --cc-on-primary: its gradient is the brand's navy
   * whatever the site's colours are, so its text colour is fixed alongside it
   * (--cc-aurora-ink), and the site's primary only shows as the marigold edge
   * glow, the hairline and the accents further down.
   */
  aurora: {
    light: `--cc-bg:#ffffff;--cc-surface:#f2f4f8;--cc-text:#16203a;--cc-muted:#55607f;--cc-border:#e4e7ee;--cc-shadow:0 26px 60px -20px rgba(13,19,36,.42),0 4px 14px -6px rgba(13,19,36,.14);--cc-radius:20px;--cc-rb:16px;--cc-rc:14px;--cc-tail:6px;--cc-aurora-ink:#f7f9ff;--cc-scroll:#c6ccda;--cc-scroll-hi:#a2abc0;--cc-font:${SYSTEM};--cc-font-head:${GEOMETRIC};`,
    dark: `--cc-bg:#131a2c;--cc-surface:#0d1324;--cc-text:#eef1fa;--cc-muted:#a3adc8;--cc-border:#283149;--cc-scroll:#39435c;--cc-scroll-hi:#525e7d;--cc-shadow:0 26px 60px -20px rgba(0,0,0,.7),0 4px 14px -6px rgba(0,0,0,.5);`,
    css: (s, o) => `
.launcher{position:relative;width:58px;height:58px;border-radius:${bubbleCorner(s, "50%", "10px")};background-image:linear-gradient(145deg,rgba(255,255,255,.3),rgba(255,255,255,0) 58%);box-shadow:0 10px 28px -8px color-mix(in srgb,var(--cc-primary) 60%,transparent),0 4px 12px rgba(13,19,36,.3);transition:transform 220ms cubic-bezier(.2,.8,.2,1),box-shadow 220ms ease}
.launcher:hover{transform:translateY(-3px) scale(1.04);box-shadow:0 18px 38px -10px color-mix(in srgb,var(--cc-primary) 72%,transparent),0 6px 16px rgba(13,19,36,.32)}
/* The unread ring reads the badge the widget already renders (ui.tsx's Launcher); there is no second "is there news" flag to keep in step. */
.launcher::after{content:"";position:absolute;inset:0;border-radius:inherit;pointer-events:none}
.badge{box-shadow:0 0 0 2px var(--cc-bg)}
.panel,.teaser{bottom:78px}
.panel{border-radius:${bubbleCorner(s, "var(--cc-radius)", "var(--cc-tail)")};transform-origin:bottom ${s};box-shadow:var(--cc-shadow),inset 0 1px 0 rgba(255,255,255,.08)}
.teaser{border-radius:${bubbleCorner(s, "var(--cc-rb)", "var(--cc-tail)")}}
.header{position:relative;overflow:hidden;color:var(--cc-aurora-ink);padding:18px 18px 20px;background:linear-gradient(104deg,#0b1020 0%,#17213d 22%,#27307a 44%,#4a2f74 62%,#1b2445 82%,#0d1324 100%);background-size:180% 180%;background-position:50% 50%;box-shadow:0 10px 24px -16px rgba(13,19,36,.7)}
/*
 * The marigold is an edge, never a wash. A radial bloom laid over the navy
 * field mixed to olive behind the avatar, so the accent now lives entirely on
 * the header's bottom edge: a crisp hairline, its tight glow, and one small
 * bloom tucked into the launcher-side corner that a screen blend keeps warm
 * and light instead of muddy. The drift below moves the navy→indigo→violet field
 * underneath it; the marigold does not move at all.
 */
.header::after{content:"";position:absolute;bottom:-46px;${s}:-24px;width:132px;height:92px;border-radius:50%;pointer-events:none;mix-blend-mode:screen;background:radial-gradient(circle,color-mix(in srgb,var(--cc-primary) 62%,transparent),transparent 66%)}
.header::before{content:"";position:absolute;left:0;right:0;bottom:0;height:1.5px;pointer-events:none;background:linear-gradient(90deg,transparent,color-mix(in srgb,var(--cc-primary) 95%,transparent) 42%,rgba(255,255,255,.4) 78%,transparent);box-shadow:0 0 12px 0 color-mix(in srgb,var(--cc-primary) 45%,transparent)}
.header > *{position:relative;z-index:1}
.header .avatar{border-radius:${bubbleCorner("right", "50%", "8px")};background:rgba(255,255,255,.16);box-shadow:inset 0 0 0 1px rgba(255,255,255,.26)}
.header h2{font-size:16.5px;letter-spacing:-.01em}
.header p{opacity:1;color:rgba(247,249,255,.8)}
.header .close{border-radius:10px}
.header .close:hover{background:rgba(255,255,255,.2)}
.status{opacity:1;color:rgba(247,249,255,.8)}
.dot{box-shadow:0 0 0 3px rgba(52,211,153,.22)}
.messages{padding:18px 16px;gap:12px}
/* Slim and quiet on both threads: the thumb colour is part of the palette (--cc-scroll), not one alpha guessed to suit light and dark at once. */
.messages,.composer textarea{scrollbar-width:thin;scrollbar-color:var(--cc-scroll) transparent}
.messages::-webkit-scrollbar,.composer textarea::-webkit-scrollbar{width:10px}
.messages::-webkit-scrollbar-track,.composer textarea::-webkit-scrollbar-track{background:transparent}
/* Styling ::-webkit-scrollbar at all brings back the legacy end buttons on Windows. */
.messages::-webkit-scrollbar-button,.composer textarea::-webkit-scrollbar-button{display:none;width:0;height:0}
.messages::-webkit-scrollbar-thumb,.composer textarea::-webkit-scrollbar-thumb{border-radius:999px;border:3px solid transparent;background:var(--cc-scroll);background-clip:padding-box}
.messages::-webkit-scrollbar-thumb:hover,.composer textarea::-webkit-scrollbar-thumb:hover{background:var(--cc-scroll-hi);background-clip:padding-box}
.bubble{border-radius:${bubbleCorner("left", "var(--cc-rb)", "var(--cc-tail)")};box-shadow:0 1px 2px rgba(13,19,36,.06),0 10px 24px -18px rgba(13,19,36,.7)}
.row.visitor .bubble{border-radius:${bubbleCorner("right", "var(--cc-rb)", "var(--cc-tail)")};background-image:linear-gradient(150deg,rgba(255,255,255,.18),rgba(255,255,255,0) 62%)}
.op-avatar{box-shadow:0 0 0 2px var(--cc-surface)}
/* Not var(--cc-primary) (the base sheet's choice): aurora's marigold is 1.6:1 on a white bubble. The underline carries the link on its own, so neither colour has to. */
.bubble a{color:var(--cc-text);text-decoration:underline}
.row.visitor .bubble a{color:var(--cc-on-primary);text-decoration:underline}
.quick-reply{background:var(--cc-bg);color:var(--cc-text);border-color:color-mix(in srgb,var(--cc-primary) 50%,var(--cc-border))}
.system{border-color:var(--cc-border)}
.composer{padding:12px;gap:10px;align-items:center}
.composer textarea{border-radius:${bubbleCorner("left", "var(--cc-rc)", "var(--cc-tail)")};padding:11px 14px}
.composer textarea:focus{outline:none;border-color:color-mix(in srgb,var(--cc-primary) 70%,var(--cc-border));box-shadow:0 0 0 3px color-mix(in srgb,var(--cc-primary) 26%,transparent)}
.composer button.send{width:44px;height:44px;border-radius:${bubbleCorner("right", "var(--cc-rc)", "var(--cc-tail)")};background-image:linear-gradient(145deg,rgba(255,255,255,.26),rgba(255,255,255,0) 60%);box-shadow:0 8px 18px -8px color-mix(in srgb,var(--cc-primary) 70%,transparent)}
.footer{padding:0 0 10px}
@keyframes cc-aur-drift{from{background-position:24% 44%}to{background-position:76% 56%}}
@keyframes cc-aur-in{from{opacity:0;transform:scale(.9) translateY(10px)}}
/* The start frame is explicit: ui.tsx puts an inline opacity:0 on the closing panel (so presets without an exit simply vanish), and an implicit start would read that back and animate 0 to 0. */
@keyframes cc-aur-out{from{opacity:1;transform:none}to{opacity:0;transform:scale(.94) translateY(8px)}}
@keyframes cc-aur-msg{from{opacity:0;transform:translateY(10px)}}
@keyframes cc-aur-breathe{50%{transform:scale(1.035);box-shadow:0 16px 34px -10px color-mix(in srgb,var(--cc-primary) 62%,transparent),0 4px 12px rgba(13,19,36,.3)}}
@keyframes cc-aur-ring{from{box-shadow:0 0 0 0 color-mix(in srgb,var(--cc-primary) 55%,transparent)}to{box-shadow:0 0 0 15px transparent}}
@media (prefers-reduced-motion: no-preference){
.header{animation:cc-aur-drift 17s ease-in-out infinite alternate}
.panel{animation:cc-aur-in 220ms cubic-bezier(.2,1.12,.32,1)}
/* The panel stays mounted for its exit (index.tsx keeps it a beat longer and marks it .closing). */
.panel.closing{animation:cc-aur-out 190ms cubic-bezier(.4,0,.9,.6)}
.launcher{animation:cc-aur-breathe 5.5s ease-in-out infinite}
/* Breathing is a resting state; a pointer on the button replaces it with the lift. */
.launcher:hover{animation:none}
.launcher:has(.badge)::after{animation:cc-aur-ring 2.1s cubic-bezier(.2,.7,.3,1) infinite}
/* Keyed rows (ui.tsx) keep their DOM node, so this plays once per message and never again on a re-render. The stagger is spent on the first screenful only. */
.row{animation:cc-aur-msg 240ms cubic-bezier(.2,.8,.2,1) both}
.messages > .row:nth-child(2){animation-delay:55ms}
.messages > .row:nth-child(3){animation-delay:110ms}
.messages > .row:nth-child(4){animation-delay:165ms}
.composer button.send{transition:transform 180ms cubic-bezier(.2,.8,.2,1)}
.composer button.send:not(:disabled):hover{transform:translateY(-2px)}
}
`,
  },
  /*
   * Forge — dark forged steel: a brushed-metal header with a silver title, one
   * ice-blue edge light, chamfered panel, bubbles and buttons. Made for game
   * and tool sites whose own pages are dark navy (Titan's "Dövme Çelik").
   * The header stays steel in the light theme too; it is the brand's metal.
   */
  forge: {
    light: `--cc-bg:#f6f8fb;--cc-surface:#e8edf3;--cc-text:#0b1220;--cc-muted:#465467;--cc-border:#c3ccd8;--cc-shadow:0 22px 50px rgba(7,11,19,.32);--cc-radius:14px;--cc-rb:7px;--cc-rc:5px;--cc-font:${SYSTEM};--cc-font-head:${INDUSTRIAL};`,
    dark: `--cc-bg:#070b13;--cc-surface:#0d131e;--cc-text:#e6edf7;--cc-muted:#97a6ba;--cc-border:#1c2635;`,
    css: () => `
.launcher{position:relative;isolation:isolate;width:58px;height:58px;border-radius:0;background:transparent;box-shadow:none;color:var(--cc-primary);filter:drop-shadow(0 10px 18px rgba(0,0,0,.45));transition:transform 160ms ease}
.launcher::before{content:"";position:absolute;inset:0;z-index:-2;background:linear-gradient(135deg,#c9d4e2,#5b6b80 38%,#1a2332 60%,var(--cc-primary));clip-path:${cut("13px")}}
.launcher::after{content:"";position:absolute;inset:2px;z-index:-1;background:linear-gradient(165deg,#2c384b,#131b28 55%,#0a0f18);clip-path:${cut("12px")}}
.launcher svg{filter:drop-shadow(0 0 6px var(--cc-primary))}
.launcher:hover{transform:translateY(-2px)}
.badge{border-radius:0;clip-path:${cut("4px")}}
.panel,.teaser{bottom:80px}
.panel{border:1px solid var(--cc-border);border-radius:0;clip-path:${cut("var(--cc-radius)")};box-shadow:none;animation:cc-forge 260ms cubic-bezier(.2,.8,.2,1)}
.panel::before,.panel::after{content:"";position:absolute;z-index:3;width:var(--cc-radius);height:var(--cc-radius);pointer-events:none;background:linear-gradient(135deg,transparent calc(50% - 1px),var(--cc-primary) calc(50% - 1px),var(--cc-primary) calc(50% + 1px),transparent calc(50% + 1px))}
.panel::before{top:-1px;left:-1px}
.panel::after{bottom:-1px;right:-1px}
@keyframes cc-forge{from{opacity:0;transform:translateY(10px)}}
.header{position:relative;background:linear-gradient(180deg,#222d3e,#121926 70%,#0e1420);color:#e6edf7;border-bottom:1px solid #26324a;padding:16px 18px}
.header::before{content:"";position:absolute;left:0;right:0;top:0;height:1px;background:linear-gradient(90deg,transparent,rgba(255,255,255,.28),transparent)}
.header::after{content:"";position:absolute;left:12%;right:12%;bottom:-1px;height:1px;background:linear-gradient(90deg,transparent,var(--cc-primary),transparent);box-shadow:0 0 12px var(--cc-primary)}
.header .avatar{border-radius:0;clip-path:${cut("8px")};background:linear-gradient(160deg,#3a475b,#1a2230);color:#dbe9f7}
.header h2{font-size:15px;letter-spacing:.09em;text-transform:uppercase;color:#eef3f9;background:linear-gradient(180deg,#ffffff 20%,#a9b8ca 80%);-webkit-background-clip:text;background-clip:text;-webkit-text-fill-color:transparent}
.status{color:#a3b1c4;opacity:1;font-size:12px;letter-spacing:.03em}
.dot{border-radius:0;transform:rotate(45deg);box-shadow:0 0 8px #34d399}
.header .close{border-radius:0;color:#cfd8e3}
.header .close:hover{background:rgba(255,255,255,.08)}
.messages{background:var(--cc-surface);background-image:linear-gradient(180deg,color-mix(in srgb,var(--cc-primary) 6%,transparent),transparent 200px)}
.bubble{background:var(--cc-bg);border:1px solid var(--cc-border);border-radius:0;clip-path:polygon(0 0,calc(100% - var(--cc-rb)) 0,100% var(--cc-rb),100% 100%,0 100%)}
.row.visitor .bubble{border:0;background:linear-gradient(160deg,var(--cc-primary),color-mix(in srgb,var(--cc-primary) 82%,#0b1a2e));clip-path:polygon(var(--cc-rb) 0,100% 0,100% 100%,0 100%,0 var(--cc-rb))}
.bubble.typing{clip-path:none}
.typing span{border-radius:0;background:var(--cc-primary)}
.meta{letter-spacing:.03em}
.op-avatar{border-radius:0;clip-path:${cut("6px")}}
.quick-reply{border-radius:0;clip-path:${cut("7px")};background:var(--cc-bg);color:var(--cc-text);border-color:var(--cc-border);font-weight:600;letter-spacing:.02em}
.quick-reply:hover,.quick-reply:focus-visible{border-color:var(--cc-primary);box-shadow:inset 0 0 0 1px var(--cc-primary)}
.faq-item,.faq-item summary,.thumb,.system,.teaser,.ask{border-radius:0}
.faq-item{background:var(--cc-bg)}
.composer{background:var(--cc-bg)}
.composer textarea{border-radius:0;background:var(--cc-surface)}
.composer textarea:focus{outline:1px solid var(--cc-primary);box-shadow:0 0 14px -6px var(--cc-primary)}
.composer button.send{border-radius:0;clip-path:${cut("8px")}}
.primary,.offline-send{border-radius:0;clip-path:${cut("8px")};text-transform:uppercase;letter-spacing:.06em}
.teaser{border-color:var(--cc-border);clip-path:${cut("10px")};box-shadow:none}
`,
  },
};

/** The dock's edge tab. Only drawn when the launcher is left to the preset. */
const dockTab = (s: "left" | "right", o: "left" | "right"): string => `
.launcher{position:fixed;top:50%;${s}:0;bottom:auto;transform:translateY(-50%);width:auto;height:auto;flex-direction:column;gap:10px;padding:16px 11px;border-radius:${s === "right" ? "12px 0 0 12px" : "0 12px 12px 0"};box-shadow:var(--cc-shadow);transition:${s} 300ms cubic-bezier(.2,.8,.2,1),padding 160ms ease}
.launcher:hover{transform:translateY(-50%);padding-${o}:15px}
.launcher .launcher-label{writing-mode:vertical-rl;transform:rotate(180deg);font-size:14px}
.panel ~ .launcher{${s}:min(400px,100vw)}
.badge{top:-8px;${s}:auto;${o}:-8px}
.teaser{position:fixed;top:50%;bottom:auto;${s}:60px;transform:translateY(-50%)}
@media (max-width:480px){.panel ~ .launcher{display:none}}
`;

/** A launcher shape chosen over the preset's own. */
function launcherOverride(shape: "circle" | "square" | "pill"): string {
  const size =
    shape === "pill"
      ? `width:auto;height:52px;padding:0 20px 0 16px;gap:8px;border-radius:999px`
      : `width:56px;height:56px;padding:0;border-radius:${shape === "circle" ? "50%" : "var(--cc-rc)"}`;
  // background-color, not background: keeps a preset's gloss while undoing
  // neon's transparent button (whose shape was its ::before).
  return `\n.launcher{${size};background-color:var(--cc-primary)}\n.launcher::before{content:none}\n`;
}

function themed(config: WidgetConfig, selector: string, body: string): string {
  if (!body) return "";
  if (config.theme === "dark") return `${selector}{${body}}\n`;
  if (config.theme === "auto") return `@media (prefers-color-scheme: dark){${selector}{${body}}}\n`;
  return "";
}

/** True when anything beyond the base sheet has to be generated. */
function isTuned(config: WidgetConfig): boolean {
  return (
    config.radius != null ||
    (config.font !== undefined && config.font !== "preset") ||
    (config.launcherStyle !== undefined && config.launcherStyle !== "preset")
  );
}

export function lookCss(config: WidgetConfig, side: "left" | "right"): string {
  const preset = presetOf(config);
  if (preset === "classic" && !isTuned(config)) return "";

  const other = side === "right" ? "left" : "right";
  const sheet = SHEETS[preset];
  const host = `:host([data-preset="${preset}"])`;
  let css = `
${host}{${sheet.light}font-family:var(--cc-font)}
${themed(config, host, sheet.dark)}.header h2,.offline strong,.prechat strong,.rating strong{font-family:var(--cc-font-head)}
.bubble,.faq-item,.faq-item summary,.thumb,.teaser,.ask{border-radius:var(--cc-rb)}
.composer textarea,.composer button.send,.field input,.offline input,.offline textarea,.primary,.offline-send{border-radius:var(--cc-rc)}
.launcher{gap:8px}
.launcher-label{font-weight:600;font-size:15px;white-space:nowrap}
${sheet.css(side, other)}`;

  const shape = launcherShape(config);
  if (config.launcherStyle && config.launcherStyle !== "preset") {
    css += launcherOverride(shape as "circle" | "square" | "pill");
  } else if (shape === "tab") {
    css += dockTab(side, other);
  }

  // Fine-tuning comes after the preset so it wins, but before the tail so a
  // phone still gets its full-screen panel.
  let tune = "";
  if (config.radius != null) {
    const r = Math.max(0, Math.min(28, Math.round(config.radius)));
    tune += `--cc-radius:${r}px;--cc-rb:${Math.round(r * 0.8)}px;--cc-rc:${Math.round(r * 0.6)}px;`;
  }
  if (config.font && config.font !== "preset" && config.font in FONT_STACKS) {
    const stack = FONT_STACKS[config.font];
    tune += `--cc-font:${stack};--cc-font-head:${stack};`;
  }
  if (tune) css += `${host}{${tune}}\n`;

  return (
    css +
    `@media (max-width:480px){.panel{position:fixed;inset:0;width:100vw;max-width:100vw;height:100dvh;max-height:100dvh;border-radius:0;border:none;box-shadow:none;clip-path:none}.panel::before,.panel::after{content:none}.panel ~ .launcher{display:none}}
@media (prefers-reduced-motion: reduce){.panel,.teaser,.row,.typing span{animation:none}.launcher,.composer button.send{transition:none}}
`
  );
}

/** The suggested colours for a preset, for the panel's "apply" choice. */
export function suggestedColours(preset: PresetId): { primaryColor: string; onPrimaryColor: string } {
  return { primaryColor: PRESETS[preset].primary, onPrimaryColor: PRESETS[preset].onPrimary };
}
